import { useCallback, useEffect, useState, useRef } from "react";
import { ILdapNode } from "./models";
import { apiPost, apiPut } from "./api";
import { getHandler } from "./components/live_table/actions";
import {
    Row,
    Col,
    Nav,
    Form,
} from "react-bootstrap";
import { Modal } from "./components/utils/modal";
import Loading from "./Loading";
import { useWindowDimensions } from "./hooks";
import { useLocalStorage } from "react-use-storage";
import React from "react";
import Dropdown from "react-bootstrap/Dropdown";
import InputField from "./components/input/InputField";
import { Alert } from "./components/alert/Alert";
import "./LdapTree.scss";

type LdapLoadFn = (
    path: string,
    filter: string,
    page: number,
    search: string
) => Promise<ILdapNode[]>;


enum LdapModalType {
    None = "",
    CreateGroup = "Создать группу",
    EditGroup = "Редактировать группу",
    EditServer = "Редактировать устройство",
    DeleteGroup = "Удалить группу",
    MoveGroup = "Переместить группу",
}

interface IStatusIconProps {
    node: ILdapNode;
}

export function StatusIcon({ node }: IStatusIconProps) {
    if (node.attrs?.status)
        return (
            <div className="peer-label">
                <div
                    title={node.cn}
                    className={
                        "platform-icon platform-" +
                        node.attrs.platform +
                        " status-" +
                        node.attrs.status
                    }
                ></div>
            </div>
        );
    else return <i className={node.icon}></i>;
}

interface CustomToggleProps {
    onClick: React.MouseEventHandler<HTMLDivElement>;
    children: React.ReactNode;
    dns: string[]
}

interface IStatusPath {
    dns: string[];
    useHref: boolean
    onClick?: (event: any, clickDn: string) => void;
    setFromGroup?: (selectDn: string) => void;
    nodes?: ILdapNode[]
    idNode?: any
}


const StatusPathToggle = React.forwardRef<HTMLDivElement, CustomToggleProps>((props, ref) => {
    const { dns, onClick, children } = props;

    const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        const targetElement = e.target as HTMLElement;
        const isAnchor = targetElement.tagName.toLowerCase() === 'a';

        if (dns.length >= 2 && !isAnchor) {
            onClick(e);
        }
    };


    return (
        <div
            ref={ref}
            onClick={handleClick}
            className={"breadcrumb d-block"}
        >
            {children}
        </div>
    );
});


export function StatusPath({ dns, onClick, useHref, nodes, idNode }: IStatusPath) {
    const [selectedDn, setSelectedDn] = useState(dns[0]);
    const [showMenu, setShowMenu] = useState(false);

    const handleClickDn = (e: any) => {
        e.preventDefault();
        let path: string = e.target.getAttribute("data-path")
        if (onClick) {
            onClick(e, path)
        }
    }

    const handleSelectDn = (e: any) => {
        setSelectedDn(e);
        setShowMenu(false)
        if (nodes && idNode) {
            nodes[idNode].selectedDn = selectedDn
        }
    }


    const dnToParts = (dn: string, useIcon: boolean = false, asHref: boolean = false) => {
        const parts = dn.split(",");
        const values: { path: string, value: string }[] = [];
        let currentPath = '';

        parts.forEach((part) => {
            const keyValue = part.split('=');

            if (keyValue.length > 1) {
                const value = keyValue[1].trim();

                if (currentPath !== '') {
                    currentPath += ',';
                }
                currentPath += part;

                values.push({ path: currentPath, value: value });
            }
        });

        return (
            <Row className="d-block">
                <Col xs>
                    <ol className="breadcrumb p-0 small d-block">
                        <div className="d-flex">
                            {values.map((value, index) => (
                                <li
                                    key={index}
                                    className={`breadcrumb-item ${index === values.length - 1 ? 'active' : ''
                                        }`}
                                    style={{ whiteSpace: 'nowrap' }}
                                >
                                    {asHref &&
                                        <a
                                            href="/#"
                                            onClick={handleClickDn}
                                            className="text-decoration-none rd-ldap-path__link"
                                            data-path={value.path}
                                        >
                                            {value.value}
                                        </a>
                                    }
                                    {!asHref && value.value}
                                </li>
                            ))}
                            {(dns.length >= 2 && useIcon) && (<li><i className="fas fa-arrow-down fa-xs pl-1"></i></li>)}
                        </div>
                    </ol>
                </Col>
            </Row>
        );
    };

    return (
        <Dropdown
            onSelect={handleSelectDn}
        >
            <Dropdown.Toggle dns={dns} as={StatusPathToggle} id="dropdown-custom-components" >
                {dnToParts(selectedDn, true, useHref)}
            </Dropdown.Toggle>

            <Dropdown.Menu show={showMenu} as="div" id="dropdown-custom-components" style={{ fontSize: 15 }}>
                {dns.map((value, index) => (
                    <Dropdown.Item as="div" key={value} eventKey={value}>
                        {dnToParts(value, false, false)}
                    </Dropdown.Item>
                ))}
            </Dropdown.Menu>
        </Dropdown>
    );
}


enum LdapAction {
    Click,
    Load
}

interface ILdapTreeNodeProps {
    node: ILdapNode;
    level: number;
    load: LdapLoadFn;
    currentDn: string;
    seed: number;
    onClick: (node: ILdapNode, action: LdapAction) => void;
    onDragStart: (event: React.DragEvent<HTMLDivElement>) => void;
    onDrop: (event: React.DragEvent<HTMLDivElement>) => void;
}

function LdapTreeNode({
    node,
    currentDn,
    load,
    level,
    seed,
    onClick,
    onDragStart,
    onDrop,
}: ILdapTreeNodeProps) {
    const [nodes, setNodes] = useState<ILdapNode[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const [collapsed, setCollapsed] = useLocalStorage(node.dn, level > 1);
    const [loading, setLoading] = useState<boolean>(false);
    const [dragOver, setDragOver] = useState<boolean>(false);
    const [dragging, setDragging] = useState<boolean>(false);
    const dragDepth = useRef(0);

    const loadNode = useCallback(
        (loadPath: string): void => {
            setError(null);
            setLoading(true);
            setCollapsed(false);
            load(
                loadPath,
                "(|(objectClass=container)(objectClass=nsContainer)(objectClass=organizationalRole)(objectClass=organizationalUnit))",
                0,
                ""
            )
                .then((nodes) => {

                    setNodes(nodes);
                    setCollapsed(false);

                    for (const child of nodes) {
                        if (currentDn >= child.dn && currentDn.indexOf(child.dn) === 0) {
                            onClick(child, LdapAction.Load);
                            break;
                        }
                    }
                })
                .catch(setError)
                .finally(() => setLoading(false));
        },
        [load, setCollapsed, onClick, currentDn]
    );

    const shouldReload = node.dn === currentDn ? seed : seed < 0 ? seed : 0;

    useEffect(() => {
        if (!collapsed) loadNode(node.dn);
        // Ignore collapsed deps warning
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [node.dn, shouldReload]);

    const renderChildren = () => {
        if (!nodes.length) return <div />;
        return (
            <div className={level ? "rd-ldap-tree__children" : "rd-ldap-tree"}>
                {error ? (
                    <Alert message={error.message} type="error" />
                ) : (
                    nodes
                        .filter((child) => !child.leaf)
                        .map((child: ILdapNode) => (
                            <LdapTreeNode
                                level={level + 1}
                                key={child.dn}
                                node={child}
                                load={load}
                                onClick={onClick}
                                currentDn={currentDn}
                                seed={seed}
                                onDragStart={onDragStart}
                                onDrop={onDrop}
                            />
                        ))
                )}
            </div>
        );
    };

    if (level === 0) {
        return loading ? (
            <i className="fa fa-spinner fa-spin"></i>
        ) : (
            renderChildren()
        );
    } else {
        return (
            <div className="rd-ldap-tree__item">
                <div
                    id={node.dn}
                    className={
                        "rd-ldap-tree__row" +
                        (node.dn === currentDn ? " rd-ldap-tree__row--selected" : "") +
                        (dragOver ? " rd-ldap-tree__row--drag-over" : "") +
                        (dragging ? " rd-ldap-tree__row--dragging" : "")
                    }
                    draggable={!node.readonly}
                    style={{ paddingLeft: `${level}em` }}
                    onDragStart={(e: React.DragEvent<HTMLDivElement>) => {
                        setDragging(true);
                        onDragStart(e);
                    }}
                    onDragEnd={() => {
                        setDragging(false);
                        setDragOver(false);
                        dragDepth.current = 0;
                    }}
                    onDrop={(e: React.DragEvent<HTMLDivElement>) => {
                        setDragOver(false);
                        setDragging(false);
                        dragDepth.current = 0;
                        onDrop(e);
                    }}
                    onDragOver={(event) => event.preventDefault()}
                    onDragEnter={() => {
                        dragDepth.current++;
                        setDragOver(true);
                    }}
                    onDragLeave={() => {
                        dragDepth.current--;
                        if (dragDepth.current <= 0) {
                            dragDepth.current = 0;
                            setDragOver(false);
                        }
                    }}
                    onClick={() => {
                        collapsed && loadNode(node.dn);
                        collapsed || setCollapsed(true);
                        onClick(node, LdapAction.Click);
                    }}
                >
                    {node.leaf ? (
                        <span className="rd-ldap-tree__toggle rd-ldap-tree__toggle--hidden">
                            <i className="fa fa-angle-right"></i>
                        </span>
                    ) : collapsed ? (
                        <span className="rd-ldap-tree__toggle">
                            <i className="fa fa-angle-right"></i>
                        </span>
                    ) : (
                        <span className="rd-ldap-tree__toggle">
                            <i className="fa fa-angle-down"></i>
                        </span>
                    )}
                    <span className="rd-ldap-tree__label">{node.cn}</span>
                    {loading && <i className="fa fa-spinner fa-spin rd-ldap-tree__spinner"></i>}
                </div>
                {!collapsed && renderChildren()}
            </div>
        );
    }
}

interface ILdapTreeProps {
    dn: string;
    load: LdapLoadFn;
    seed: number;
    onClick: (node: ILdapNode, action: LdapAction) => void;
    onDragDrop: (src: string, dest: string) => void;
}

export function LdapTree({
    dn,
    load,
    seed,
    onClick,
    onDragDrop,
}: ILdapTreeProps) {
    const root: ILdapNode = {
        dn: "",
        cn: "",
        attrs: { objectClass: ["domain"] },
        leaf: false,
        icon: "fa fa-file",
        member_group: [""]
    };

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>) => {
        event.dataTransfer.setData("text", event.currentTarget.id);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        const id = event.dataTransfer.getData("text");
        const targetId = event.currentTarget.id;
        onDragDrop(id, targetId);
    };

    return (
        <LdapTreeNode
            node={root}
            load={load}
            onClick={onClick}
            level={0}
            currentDn={dn}
            seed={seed}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
        />
    );
}

interface ILdapListProps {
    load: LdapLoadFn;
    dn: string;
    onClick: (node: ILdapNode, action: LdapAction) => void;
    search: string;
    setDn: (dn: string) => void;
    seed: number;
}

export function LdapList({ load, dn, onClick, setDn, search, seed }: ILdapListProps) {
    const [nodes, setNodes] = useState<ILdapNode[]>([]);
    const [error, setError] = useState<Error | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [page, setPage] = useState<number>(0);
    // eslint-disable-next-line
    const [alias, setAlias] = useState<any>("");
    const [displayNames, setDisplayName] = useState<{ [key: string]: string }>({});
    const path = dn;

    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        nodes.forEach((node, index) => {
            setDisplayName((displayNames) => ({ ...displayNames, [node.attrs.id || ""]: node.attrs.alias_or_display_name }));
        });
    }, [nodes]);

    const loadNode = useCallback(
        (loadPath: string, page: number, nodes: ILdapNode[], search: string): void => {
            setError(null);
            setLoading(true);
            load(loadPath, "(objectClass=*)", page, search)
                .then((newNodes) => {
                    if (page > 0) {
                        nodes.pop();
                        setNodes([...nodes, ...newNodes]);
                        setTimeout(() => {
                            scrollRef.current!.scrollIntoView();
                        }, 100);
                    } else {
                        setNodes(newNodes);
                    }
                })
                .catch(setError)
                .finally(() => setLoading(false));
        },
        [load]
    );

    useEffect(() => {
        setNodes([]);
        setPage(0);
    }, [path]);

    useEffect(() => {
        if (path) loadNode(path, page, nodes, search);
        // eslint-disable-next-line
    }, [path, loadNode, page, search, seed]);


    let updateAlias = (node: ILdapNode, alias: string) => {
        apiPut<any>("/ldap/edit/", {
            server_id: node.attrs.id || "",
            alias: alias,
        })
            .then((result) => {
                setDisplayName({ ...displayNames, [node.attrs.id || ""]: alias });
                setAlias("");
            })
            .catch((err) => {
                alert(err.message)

                if (err.params && typeof err.params === 'object') {
                    const textErrors = [];
                    for (const field in err.params) {
                        if (err.params.hasOwnProperty(field) && Array.isArray(err.params[field])) {
                            const errors = err.params[field].join(', ');
                            textErrors.push(`${field}: ${errors}`);
                        }
                    }
                    if (textErrors.length > 0) {
                        const errorMessage = `Validation error. Text field errors:\n${textErrors.join('; ')}`;
                        alert(errorMessage);
                    }
                }
            });
    }

    const updateExecutorEvents = useCallback((node: any) => {
        if (window.executorEventsUpdate) {
            window.executorEventsUpdate()
        }
    }, [])


    const parseAndModifyMenu = (menu: any, node: any) => {
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(menu || "", 'text/html');
        const aTag = parsedDocument.createElement('a');

        if (!parsedDocument.querySelector("a.dropdown-item")) {
            return '';
        }

        aTag.href = '#';
        aTag.id = "rename-alias"
        aTag.className = 'dropdown-item';
        aTag.innerHTML = '<span class="rd-icon-edit"></span> Переименовать';
        parsedDocument.body.querySelector('div.dropdown-menu')?.appendChild(aTag);

        return new XMLSerializer().serializeToString(parsedDocument);
    };


    let updateAliasEvent = (e: any, node: ILdapNode) => {
        if (e.target.id !== "rename-alias") {
            return
        }

        setAlias(<LdapModelEdit
            node={node!}
            baseValue={displayNames[node.attrs.id || ""] || node.attrs.alias_or_display_name}
            modalType={LdapModalType.EditServer}
            onSuccess={(value) => { updateAlias(node, value) }}
            onCancel={() => { setAlias("") }}
        />)
    }

    if (loading)
        return (
            <div className="rd-ldap-list__loading">
                <Loading />
            </div>
        );

    if (error) return <Alert message={error.message} type="error" />;

    if (!nodes.length && search.length) {
        return <div className="rd-ldap-list__empty">Не найдены устройства, удовлетворяющие заданному фильтру</div>;
    }

    if (!nodes.length)
        return <div className="rd-ldap-list__empty">Пустой контейнер</div>;

    const hasAnyMenu = nodes.some((n) => n.menu);

    return (
        <div>
            {alias}
            <table className="rd-table">
                <thead>
                    <tr>
                        <th className="rd-table__col-icon"></th>
                        <th>Название</th>
                        {search && <th>Путь до объекта</th>}
                        {!search && <th>Описание</th>}
                        {hasAnyMenu && <th className="rd-table__col-actions"></th>}
                    </tr>
                </thead>
                <tbody ref={updateExecutorEvents}>
                    {nodes.map((node) => {
                        let currentDisplayName = displayNames[node.attrs.id || ""] || node.attrs.alias_or_display_name || node.cn
                        let serverID = node.attrs.id ? `(${node.attrs.id})` : ""
                        if (node.page)
                            return (
                                <tr key="next page">
                                    <td colSpan={hasAnyMenu ? 4 : 3} style={{ textAlign: 'center', padding: '12px 0' }}>
                                        <span
                                            role="button"
                                            className="rd-ldap-list__load-more"
                                            onClick={() => setPage(node.page!)}
                                        >
                                            Загрузить еще...
                                        </span>
                                    </td>
                                </tr>
                            );
                        else
                            return (
                                <tr key={node.dn}>
                                    <td className="rd-table__col-icon">
                                        <StatusIcon node={node} />
                                    </td>
                                    <td>{currentDisplayName} {serverID}</td>
                                    {search && (
                                        <td>
                                            <StatusPath useHref={true} dns={node.member_group || [""]} onClick={(e, clickDn) => { setDn(clickDn) }} />
                                        </td>
                                    )}
                                    {!search && <td>{node.attrs?.description}</td>}
                                    {hasAnyMenu && (
                                        <td
                                            className="rd-table__col-actions"
                                            onClick={(e) => { updateAliasEvent(e, node) }}
                                            id={node.id}
                                        >
                                            {node.menu && (
                                                <div
                                                    className="rd-ldap-list__actions"
                                                    ref={(el) => {
                                                        if (!el) return;
                                                        el.querySelectorAll<HTMLButtonElement>("[data-handler]").forEach((btn) => {
                                                            if (btn.dataset.handlerBound) return;
                                                            btn.dataset.handlerBound = "1";
                                                            btn.addEventListener("click", (e) => {
                                                                e.preventDefault();
                                                                const handler = getHandler(btn.dataset.handler!);
                                                                const config = btn.dataset.handlerConfig
                                                                    ? JSON.parse(btn.dataset.handlerConfig)
                                                                    : {};
                                                                handler({
                                                                    source: "change_form",
                                                                    actionName: btn.dataset.handler!,
                                                                    selectedRows: [{ pk: node.attrs.id }],
                                                                    optionValues: {},
                                                                    config,
                                                                });
                                                            });
                                                        });
                                                    }}
                                                    dangerouslySetInnerHTML={{
                                                        __html: parseAndModifyMenu(node.menu, node)
                                                    }}
                                                />
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                    })}
                </tbody>
            </table>
            <div ref={scrollRef} />
        </div>
    );
}

interface ILdapBrowserProps {
    node?: ILdapNode;
    onClick: (node: ILdapNode, action: LdapAction) => void;
}

export function LdapBrowserNav({ node, onClick }: ILdapBrowserProps) {
    const items = node?.dn.split(",") || [];

    let pills = [];
    let path = [];
    for (const item of items) {
        const itemName = item.split("=")[1] || item;
        path.unshift(item);
        pills.push({ cn: itemName, dn: path.join(",") });
    }

    return (
        <Col lg={12}>
            <Nav variant="pills" className="rd-ldap-nav">
                {pills.map((pill: any, i: number) => (
                    <Nav.Item key={pill.dn} onClick={() => onClick(pill, LdapAction.Click)}>
                        <Nav.Link>{pill.cn}</Nav.Link>
                        {i + 1 === pills.length || (
                            <i className="fas fa-angle-double-right"></i>
                        )}
                    </Nav.Item>
                ))}
            </Nav>
        </Col>
    );
}

interface ILdapModalEditProps {
    text?: string;
    baseValue?: string
    node: ILdapNode;
    modalType: LdapModalType;
    onCancel: () => void;
    onSuccess: (value: string, setErrorMassage: any) => void;
}

export function LdapModelEdit({
    text,
    baseValue,
    modalType,
    node,
    onCancel,
    onSuccess,
}: ILdapModalEditProps) {
    const doCreate = modalType === LdapModalType.CreateGroup;
    const doEdit = modalType === LdapModalType.EditGroup;
    const doEditServer = modalType === LdapModalType.EditServer;
    const doDelete = modalType === LdapModalType.DeleteGroup;
    const doMove = modalType === LdapModalType.MoveGroup;

    const [value, setValue] = useState<string>(baseValue || (doCreate ? "" : node.cn));
    const [errorMassage, setErrorMassage] = useState<string>("");
    const title = modalType.valueOf();

    return (
        <Modal
            show={true}
            onClose={onCancel}
            title={title + (doCreate ? " в " + node.cn : "")}
            size="sm"
            bodyClassName="pt-3"
            footer={
                <>
                    <button
                        className="rd-btn rd-btn--primary rd-btn--medium"
                        onClick={() => onSuccess(value, setErrorMassage)}
                    >
                        Подтвердить
                    </button>
                    <button
                        className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium"
                        onClick={onCancel}
                    >
                        Закрыть
                    </button>
                </>
            }
        >
            {errorMassage && (
                <Alert
                    message={errorMassage}
                    type="error"
                    onClose={() => setErrorMassage("")}
                />
            )}
            {(doCreate || doEdit || doEditServer) && (
                <InputField
                    name="ldap_value"
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    label={title}
                />
            )}
            {doDelete && (
                <Form.Label>
                    Вы действительно хотите удалить группу {node.cn}?
                </Form.Label>
            )}
            {doMove && text}
        </Modal>
    );
}

export function LdapBrowser() {
    const { height } = useWindowDimensions();

    const divRef = useRef<HTMLDivElement>(null);

    const [dn, setDn] = useLocalStorage<string>("tree_location", "dc=local");

    // Current selected node and drop node for drag and drop
    const [edit, setEdit] = useState<ILdapNode | null>(null);
    const [drop, setDrop] = useState<string>("");

    // Modal dialog state
    const [modal, setModal] = useState<LdapModalType>(LdapModalType.None);
    const [modalText, setModalText] = useState<string>("");

    // Used to reload tree
    const [seed, setSeed] = useState<number>(0);

    // Used to filter objects
    const [search, setSearch] = useState<string>("");
    const [inputValue, setInputValue] = useState<string>("");
    const clearSearchRef = useRef<HTMLInputElement>(null);

    // Global error alert
    const [browserError, setBrowserError] = useState<string>("");

    let timer: NodeJS.Timeout;

    // Used to not trigger loading on every keyboard tap
    const updateSearch = (value: string) => {
        setInputValue(value);
        if (timer) clearTimeout(timer);
        timer = setTimeout(() => setSearch(value), 350);
    }

    const clearSearch = () => {
        setSearch("");
        setInputValue("");
        if (clearSearchRef.current) {
            clearSearchRef.current.value = "";
        }
    };

    const load = useCallback((dn: string, ldapFilter: string, page: number, search: string) => {
        return apiPost<ILdapNode[]>("/ldap/search/", {
            dn: dn,
            filter: ldapFilter,
            page: page,
            search: search,
        }).catch((err) => {
            setBrowserError(err.params?.detail || err.message || "Ошибка загрузки");
            throw err;
        });
    }, []);

    const goTo = (node: ILdapNode, action: LdapAction) => {
        setEdit(node);
        if (action === LdapAction.Click) {
            setDn(node.dn);
        }
    };

    const doEdit = (value: string, setErrorMassage: any) => {
        apiPost<any>("/ldap/edit/", {
            dn: edit!.dn,
            value: value,
            editType: modal,
            drop: drop,
        })
            .then((result) => {
                setDn(result.dn);
                if (modal === LdapModalType.MoveGroup) {
                    setSeed(-Math.random()); // Reload all nodes
                } else {
                    setSeed(Math.random()); // Reload currnet node
                }
                setModal(LdapModalType.None);
            })
            .catch((err) => {
                setErrorMassage(err.params?.detail || err.message || "Произошла ошибка");
            });
    };

    const cn = (dn: string) => dn.split(",").at(-1)?.split("=")[1];

    const dragDrop = (drag: string, drop: string) => {
        if (drag === drop) return;
        setEdit({ dn: drag } as ILdapNode);
        setDn(drag);
        setDrop(drop);
        setModalText("Переместить группу " + cn(drag) + " в " + cn(drop) + "?");
        setModal(LdapModalType.MoveGroup);
    };

    const myHeight =
        height - (divRef.current?.getBoundingClientRect().top || 0) - 100;

    return (
        <Row ref={divRef} className="px-3">
            {browserError && (
                <Col lg={12} className="pt-3">
                    <Alert
                        message={browserError}
                        type="error"
                        onClose={() => setBrowserError("")}
                    />
                </Col>
            )}
            <Col lg={12} className="rd-ldap-browser__toolbar pt-3">
                <div className="rd-ldap-browser__actions">
                    <button
                        className="rd-btn rd-btn--outline rd-btn--primary rd-btn--medium"
                        onClick={() => setModal(LdapModalType.CreateGroup)}
                        title={LdapModalType.CreateGroup.valueOf()}
                    >
                        <span className="rd-icon-plus"></span>
                        Создать группу
                    </button>
                    {!edit?.readonly && (
                        <button
                            className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium rd-btn--icon-only"
                            onClick={() => setModal(LdapModalType.DeleteGroup)}
                            title={LdapModalType.DeleteGroup.valueOf()}
                        >
                            <span className="rd-icon-delete"></span>
                        </button>
                    )}
                    {!edit?.readonly && (
                        <button
                            className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium rd-btn--icon-only"
                            onClick={() => setModal(LdapModalType.EditGroup)}
                            title={LdapModalType.EditGroup.valueOf()}
                        >
                            <span className="rd-icon-edit"></span>
                        </button>
                    )}
                    {edit?.url && (
                        <button
                            className="rd-btn rd-btn--outline rd-btn--secondary rd-btn--medium rd-btn--icon-only"
                            onClick={() => window.open(edit!.url, "_self")}
                            title="Свойства объекта"
                        >
                            <span className="rd-icon-export"></span>
                        </button>
                    )}
                </div>
                <div className="rd-ldap-browser__search">
                    <div className={`rd-input rd-input--medium rd-input--search${inputValue ? " has-value" : ""}`}>
                        <div className="rd-input__wrapper">
                            <span className="rd-input__search-icon rd-icon-search"></span>
                            <input
                                type="text"
                                className="rd-input__field"
                                placeholder="Введите значение"
                                onChange={(ev) => updateSearch(ev.target.value)}
                                ref={clearSearchRef}
                            />
                            <button
                                className={`rd-input__clear${inputValue ? " is-visible" : ""}`}
                                onClick={clearSearch}
                            >
                                <span className="rd-icon-cross"></span>
                            </button>
                        </div>
                    </div>
                </div>
            </Col>
            <Col lg={4} className="d-sm-none d-lg-block">
                <div className="rd-ldap-scroll-container" style={{ height: myHeight }}>
                    <LdapTree
                        load={load}
                        dn={dn}
                        seed={seed}
                        onClick={goTo}
                        onDragDrop={dragDrop}
                    />
                </div>
            </Col>
            <Col lg={8} sm={12} style={{"padding": "0px 20px"}}>
                <div className="rd-ldap-scroll-container rd-ldap-scroll-container--list" style={{ height: myHeight }}>
                    <LdapList load={load} dn={dn} onClick={goTo} search={search} setDn={setDn} seed={seed} />
                </div>
            </Col>
            {modal && (
                <LdapModelEdit
                    text={modalText}
                    node={edit!}
                    modalType={modal}
                    onCancel={() => setModal(LdapModalType.None)}
                    onSuccess={doEdit}
                />
            )}
        </Row>
    );
}
